import { useState } from 'react';
import { useReduxState, useDispatch } from 'hooks';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, TextArea } from 'components/common/form';

import type { Course } from 'shared-types';

interface Props {
  onImportSuccess?: () => void;
  targetLanguage: Course['language'];
}

const SettingsImportLanguageVersions = ({
  onImportSuccess,
  targetLanguage
}: Props) => {
  const {
    messages,
    pageState: { course }
  } = useReduxState();
  const { dispatch } = useDispatch();

  const [json, setJson] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  // validate JSON input
  const validateJson = (value: string) => {
    try {
      JSON.parse(value.trim());
      return undefined;
    } catch (error) {
      // intentionally log the JSON error to console for debugging in production
      console.error(error);
      return messages.courseLanguageVersionsInvalidJson;
    }
  };

  const handleValueChange = (value: string) => {
    setJson(value);
    setError(validateJson(value));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    setTimeout(() => {
      setError(validateJson(target.value));
    });
  };

  const handleSubmit = async () => {
    const err = validateJson(json);
    setError(err);
    if (!err) {
      try {
        const parsedData = JSON.parse(json.trim());
        await api.post(
          `/admin/courses/${course.id}/import-language-version-data/${targetLanguage}`,
          parsedData
        );

        dispatch.addNotice({
          text: messages.commonSavedSuccessfully,
          type: 'success'
        });

        if (onImportSuccess) onImportSuccess();
      } catch (error) {
        await dispatch.handleErrorDispatch(error);
      }
    }
  };

  return (
    <ModalButtonWithForm
      buttonIsDropdown={true}
      buttonIcon={'file-import'}
      buttonCategory="success"
      buttonText={messages.coursesImportJson}
      modalActionText={messages.commonImport}
      modalTitle={messages.coursesImportJson}
      onSubmit={handleSubmit}
      submitDisabled={!json || !!error}
    >
      {() => (
        <>
          <FormGroup htmlFor="json" label="JSON">
            <TextArea
              error={error}
              field="json"
              id="json"
              rows={8}
              initialValue={json}
              onValueChange={handleValueChange}
              onPaste={handlePaste}
              validateOnChange={true}
            />
          </FormGroup>
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default SettingsImportLanguageVersions;
